import { useEffect, useState } from 'react';

interface ConfettiProps {
  active: boolean;
  onComplete?: () => void;
}

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  velocity: {
    x: number;
    y: number;
  };
  rotationSpeed: number;
}

export default function Confetti({ active, onComplete }: ConfettiProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  const colors = [
    '#ff00ff', // neon pink
    '#8b5cf6', // cyber purple
    '#06b6d4', // cyber blue
    '#fbbf24', // cyber gold
    '#ffffff', // white
    '#f59e0b', // orange
  ];

  useEffect(() => {
    if (!active) {
      setPieces([]);
      return;
    }

    // Create confetti pieces
    const newPieces: ConfettiPiece[] = [];
    for (let i = 0; i < 100; i++) {
      newPieces.push({
        id: i,
        x: Math.random() * window.innerWidth,
        y: -10,
        rotation: Math.random() * 360,
        color: colors[Math.floor(Math.random() * colors.length)],
        velocity: {
          x: (Math.random() - 0.5) * 4,
          y: Math.random() * 3 + 2,
        },
        rotationSpeed: (Math.random() - 0.5) * 5,
      });
    }
    setPieces(newPieces);

    // Animation loop
    const animationDuration = 3000; // 3 seconds
    const startTime = Date.now();

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;

      if (elapsed >= animationDuration) {
        setPieces([]);
        onComplete?.();
        return;
      }

      setPieces(currentPieces => 
        currentPieces.map(piece => ({
          ...piece,
          x: piece.x + piece.velocity.x,
          y: piece.y + piece.velocity.y,
          rotation: piece.rotation + piece.rotationSpeed,
          velocity: {
            ...piece.velocity,
            y: piece.velocity.y + 0.1, // gravity
          },
        })).filter(piece => piece.y < window.innerHeight + 10)
      );

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [active, onComplete]);

  if (!active || pieces.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {pieces.map(piece => (
        <div
          key={piece.id}
          className="absolute w-2 h-2 rounded-sm"
          style={{
            left: piece.x,
            top: piece.y,
            backgroundColor: piece.color,
            transform: `rotate(${piece.rotation}deg)`,
            boxShadow: `0 0 6px ${piece.color}`,
          }}
        />
      ))}
    </div>
  );
}